import React, { Component, Suspense } from 'react';
import Modal from 'rc-dialog';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';

import { ModalProps } from 'stores/ModalStore';
import FeedbackMenuItemAnalyticsModalStore from 'stores/Feedback/FeedbackMenuItemAnalyticsModalStore';
import { FeedbackDishGroupReportMenuItem } from 'http/Api/Feedback/types';
import RootStore from 'stores/RootStore';
import { getFormattedDate } from 'helpers/datetime';
import {
  getFeedbackAvgRatingColor,
  getFeedbackRatingColor,
} from 'helpers/feedback';
import { printPage } from 'helpers/print';

import ModalHeader from 'components/Modals/Components/ModalHeader/ModalHeader';
import ModalBody from 'components/Modals/Components/ModalBody/ModalBody';
import Spinner from 'components/Spinner/Spinner';
import PrintHeader from 'components/Print/Header/PrintHeader';
import Button from 'components/Button/Button';
import Icon from 'components/Icon/Icon';

import style from './FeedbackMenuItemAnalyticsModal.module.scss';
import AnalyticsInfoBlock from './AnalyticsInfoBlock/AnalyticsInfoBlock';

const Charts = React.lazy(() => import('../Charts/FeedbackMenuItemCharts'));

interface Props extends ModalProps {
  menuItem: FeedbackDishGroupReportMenuItem;
  startDate: string;
  endDate: string;
}

@observer
class FeedbackMenuItemAnalyticsModal extends Component<Props> {
  private _store = new FeedbackMenuItemAnalyticsModalStore();

  componentDidMount() {
    const { menuItem, startDate, endDate } = this.props;

    this._store.fetchAnalytics(menuItem.menuItemId, startDate, endDate);
  }

  render() {
    const { menuItem } = this.props;

    return (
      <Modal
        {...this.props}
        className={style.modal}
        wrapClassName={style.modalWrap}
        destroyOnClose
        animation="zoom"
        maskAnimation="fade"
        visible
      >
        <ModalHeader>
          <FormattedMessage id="modal.feedback.menuItemAnalytics.title" />
        </ModalHeader>

        <ModalBody>
          <>
            {this._store.loading && <Spinner contain />}

            <PrintHeader title={menuItem.title} description={this.period} />

            <div className={style.header}>
              <div className={style.title}>{menuItem.title}</div>
              <div className={style.period}>{this.period}</div>

              <Button
                styleType="text"
                onClick={this._handlePrint}
                className={style.printBtn}
              >
                <Icon type="print" />
              </Button>
            </div>

            <div className={style.infoBlocks}>
              <AnalyticsInfoBlock
                title={RootStore.localization.formatMessage(
                  'modal.feedback.menuItemAnalytics.avgRating',
                )}
                value={`${menuItem.avgRating}%`}
                valueColor={getFeedbackAvgRatingColor(menuItem.avgRating)}
              />
              <AnalyticsInfoBlock
                title={RootStore.localization.formatMessage(
                  'modal.feedback.menuItemAnalytics.maxRating',
                )}
                value={menuItem.maxRating.toString()}
                valueColor={getFeedbackRatingColor(menuItem.maxRating)}
              />
              <AnalyticsInfoBlock
                title={RootStore.localization.formatMessage(
                  'modal.feedback.menuItemAnalytics.minRating',
                )}
                value={menuItem.minRating.toString()}
                valueColor={getFeedbackRatingColor(menuItem.minRating)}
              />
              <AnalyticsInfoBlock
                title={RootStore.localization.formatMessage(
                  'modal.feedback.menuItemAnalytics.amount',
                )}
                value={menuItem.amount.toString()}
              />
            </div>

            <div className={style.charts}>
              <Suspense fallback={<Spinner contain />}>
                <Charts
                  ratingCountChartData={this._store.ratingCountChartData}
                  avgRatingChartData={this._store.avgRatingChartData}
                />
              </Suspense>
            </div>
          </>
        </ModalBody>
      </Modal>
    );
  }

  get period() {
    const { startDate, endDate } = this.props;
    if (startDate === endDate) return getFormattedDate(startDate);

    return `${getFormattedDate(startDate)} - ${getFormattedDate(endDate)}`;
  }

  private _handlePrint() {
    document.body.classList.add(style.printHideRoot);
    printPage();
    document.body.classList.remove(style.printHideRoot);
  }
}

export default FeedbackMenuItemAnalyticsModal;
